import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import type { InsuranceCompany, Plan } from "@/mocks/healthInsuranceCompanies";
import { useBrochureManager } from "@/hooks/useBrochureManager";

interface CompanyCardProps {
  company: InsuranceCompany;
  searchQuery: string;
}

export default function CompanyCard({ company, searchQuery }: CompanyCardProps) {
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const { logDownload } = useBrochureManager();

  const filteredPlans = useMemo(() => {
    if (!searchQuery.trim()) return company.plans;
    const q = searchQuery.toLowerCase();
    return company.plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(q) ||
        company.name.toLowerCase().includes(q) ||
        plan.bestSuitedFor.toLowerCase().includes(q)
    );
  }, [company.plans, company.name, searchQuery]);

  if (filteredPlans.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {/* Company Header */}
      <div className="p-5 md:p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-start gap-4">
          <div
            className={`w-14 h-14 rounded-xl bg-gradient-to-br ${company.logoColor} flex items-center justify-center shrink-0 shadow-sm`}
          >
            <i className={`${company.logoIcon} text-white text-2xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-slate-900">
              {company.name}
            </h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              {company.description}
            </p>
            <a
              href={company.officialBrochurePage}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-cta-600 hover:text-cta-700 font-medium mt-2 transition-colors"
            >
              <i className="ri-external-link-line"></i>
              Visit Official Brochure Page
            </a>
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="divide-y divide-slate-100">
        {filteredPlans.map((plan) => (
          <PlanRow
            key={plan.id}
            plan={plan}
            companyName={company.name}
            isExpanded={expandedPlan === plan.id}
            onToggle={() =>
              setExpandedPlan(expandedPlan === plan.id ? null : plan.id)
            }
            onDownload={() => logDownload(`${company.name} — ${plan.name}`, "brochure", plan.brochureUrl)}
          />
        ))}
      </div>
    </div>
  );
}

function PlanRow({
  plan,
  companyName,
  isExpanded,
  onToggle,
  onDownload,
}: {
  plan: Plan;
  companyName: string;
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: () => void;
}) {
  return (
    <div className="group">
      <div className="p-4 md:p-5 hover:bg-slate-50/60 transition-colors">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base md:text-lg font-semibold text-slate-800">
                {plan.name}
              </h3>
              {plan.specialNotes && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-100">
                  <i className="ri-information-line"></i>
                  Diabetes Plan
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Best suited for: {plan.bestSuitedFor}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onToggle}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i
                className={`ri-${isExpanded ? "arrow-up-s-line" : "arrow-down-s-line"}`}
              ></i>
              {isExpanded ? "Hide Details" : "View Details"}
            </button>
            <a
              href={plan.brochureUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onDownload}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors shadow-sm hover:shadow whitespace-nowrap"
            >
              <i className="ri-download-line"></i>
              Download Brochure
            </a>
            <Link
              to="/book-appointment?source=Health%20Insurers%20%26%20Brochures%20Page"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cta-600 hover:bg-cta-700 text-white text-sm font-medium transition-colors shadow-sm hover:shadow whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Talk to Advisor
            </Link>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-slate-100 animate-fade-in-up">
            {plan.specialNotes && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-100 flex items-start gap-2">
                <i className="ri-alert-line text-amber-600 text-lg shrink-0 mt-0.5"></i>
                <p className="text-sm text-amber-800 font-medium">
                  {plan.specialNotes}
                </p>
              </div>
            )}
            <h4 className="text-sm font-semibold text-slate-700 mb-2.5">
              Key Benefits
            </h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {plan.keyBenefits.map((benefit, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm text-slate-600"
                >
                  <i className="ri-check-line text-cta-500 shrink-0 mt-0.5"></i>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}